<template>
  <div>
    <div class="admin-toolbar">
      <p class="admin-subtitle">Edit the editorials shown in the home page sidebar.</p>
    </div>

    <!-- Loading and Errors -->
    <div v-if="loading" class="admin-empty">Loading editorials...</div>
    <div v-else-if="error" class="admin-empty text-red-600">{{ error }}</div>
    <div v-else-if="posts.length === 0" class="admin-empty">No editorials found.</div>

    <!-- Table -->
    <div v-else class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Subtitle</th>
            <th>Description</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="post in posts" :key="post.id">
            <td class="tabular-nums text-gray-500">{{ post.id }}</td>
            <td class="font-medium text-gray-900">{{ post.title || 'N/A' }}</td>
            <td>{{ truncateContent(post.subtitle) }}</td>
            <td class="max-w-md">{{ truncateContent(post.description) }}</td>
            <td class="text-right">
              <button @click="openEditModal(post)" class="btn-cream btn-sm">
                <i class="fa-solid fa-pen"></i> Edit
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Edit Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
        <div class="modal-panel max-w-2xl">
          <h2 class="mb-5 font-serif text-2xl font-bold text-gray-900">Edit Editorial</h2>

          <div class="space-y-4">
            <div>
              <label class="form-label">Title</label>
              <input v-model="editForm.title" type="text" class="form-input" />
            </div>
            <div>
              <label class="form-label">Subtitle</label>
              <input v-model="editForm.subtitle" type="text" class="form-input" />
            </div>
            <div>
              <label class="form-label">Description</label>
              <textarea v-model="editForm.description" rows="10" class="form-input resize-y"></textarea>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button @click="showModal = false" class="btn-ghost">Cancel</button>
            <button @click="updateEditorial" :disabled="saving" class="btn-primary">
              {{ saving ? 'Saving...' : 'Update' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api, { errorMessage } from '@/api'

// State variables
const posts = ref([])
const loading = ref(false)
const saving = ref(false)
const error = ref(null)
const showModal = ref(false)
const editForm = ref({ id: null, title: '', subtitle: '', description: '' })

// Fetch all editorials
const fetchPosts = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await api.get('/editorials')
    posts.value = response.data
  } catch (err) {
    error.value = errorMessage(err, 'Failed to fetch editorials')
  } finally {
    loading.value = false
  }
}

// Open edit modal and fill form
const openEditModal = (post) => {
  editForm.value = { ...post }
  showModal.value = true
}

// Update editorial
const updateEditorial = async () => {
  const { id, title, subtitle, description } = editForm.value
  saving.value = true
  try {
    await api.put(`/editorials/${id}`, { title, subtitle, description })
    showModal.value = false
    await fetchPosts() // refresh table
    Swal.fire({ icon: 'success', title: 'Saved', text: 'Editorial updated.', timer: 1600, showConfirmButton: false })
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage(err, 'Failed to update'),
      confirmButtonColor: '#BF3131'
    });
  } finally {
    saving.value = false
  }
}

// Truncate function
const truncateContent = (content) => {
  if (!content) return 'N/A'
  return content.length > 100 ? content.substring(0, 100) + '...' : content
}

// Initial load
onMounted(fetchPosts)
</script>
